import { useEffect, useState } from "react";
import { OwcEditableDropdown, OwcIconButton, OwcInput, OwcListItem, OwcTypography } from "@one/react";
import PropTypes from "prop-types";
import AssistantSelect from "../../AssistantSelect/AssistantSelect";

const STATUS_OPTIONS = ["PENDING", "APPROVED", "REJECTED"];

const isAssistantSelectionRequired = ({ draftStatus, approvalStatus, requiresAssistant, assistantId }) => {
  if (draftStatus !== "APPROVED") return false;
  if (approvalStatus !== "PENDING") return false;
  if (!requiresAssistant) return false;
  return !assistantId;
};

const computeCanSave = ({ hasChanges, isAssistantRequired, draftAssistantId, isRejectionReasonRequired, draftRejectionReason }) => {
  if (!hasChanges) return false;
  if (isAssistantRequired) return Boolean(draftAssistantId);
  if (isRejectionReasonRequired) return Boolean(draftRejectionReason.trim());
  return true;
};

const formatStatusLabel = (status) => {
  if (!status) return "-";
  return status.charAt(0) + status.slice(1).toLowerCase();
};

const ApprovedBadge = ({ status }) => {
  if (!status) return <OwcTypography variant="button">-</OwcTypography>;

  if (status === "APPROVED") {
    return (
      <span style={{
        display: "inline-block",
        border: "1px solid #000",
        borderRadius: "5px",
        padding: "1px 6px",
        color: "#007C6B",
        backgroundColor: "#fff",
        fontWeight: 700,
        fontSize: "0.85rem",
        lineHeight: "1.2rem",
      }}>
        Approved
      </span>
    );
  }

  return (
    <OwcTypography variant="button" style={{ color: "#1F1F1F" }}>
      {formatStatusLabel(status)}
    </OwcTypography>
  );
};

const ApprovalStatusHeader = ({
  allowEdit,
  isEditMode,
  saving,
  hasChanges,
  canSave,
  onStartEdit,
  onCancel,
  onSave,
}) => (
  <div style={{ display: "flex", alignItems: "center" }}>
    <OwcTypography variant="caption" style={{ width: "70%", color: "var(--one-color-foreground-base-1)" }}>
      Approval Status
    </OwcTypography>

    {allowEdit && !isEditMode && (
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <OwcIconButton
          icon="edit"
          type="outlined"
          flat={true}
          onClick={onStartEdit}
          style={{ color: "#0B41CD" }}
        />
      </div>
    )}

    {allowEdit && isEditMode && (
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <OwcIconButton
          icon="circle_confirm"
          type="filled"
          flat={true}
          disabled={saving || !canSave}
          onClick={onSave}
          style={{ color: canSave ? "#28A745" : "#999999" }}
          data-testid="approval-status-save-btn"
        />
        <OwcIconButton
          icon="circle_clear"
          type="filled"
          flat={true}
          disabled={saving}
          onClick={onCancel}
          style={{ color: hasChanges ? "#DC3545" : "#999999" }}
          data-testid="approval-status-cancel-btn"
        />
      </div>
    )}
  </div>
);

const ApprovalStatusEditPanel = ({
  saving,
  draftStatus,
  handleSelectChange,
  shouldShowAssistantSelect,
  users,
  draftAssistantId,
  onAssistantChange,
  draftAssistantDuration,
  onAssistantDurationChange,
  draftRejectionReason,
  onRejectionReasonChange,
}) => (
  <div style={{ opacity: saving ? 0.6 : 1, display: "flex", flexDirection: "column", gap: "0.6rem" }}>
    <OwcEditableDropdown
      style={{ width: "100%", maxWidth: "520px" }}
      label="Select status"
      value={formatStatusLabel(draftStatus)}
      disabled={saving}
      onSelectChange={handleSelectChange}
    >
      {STATUS_OPTIONS.map((status) => (
        <OwcListItem key={status} value={formatStatusLabel(status)} selected={draftStatus === status}>
          {formatStatusLabel(status)}
        </OwcListItem>
      ))}
    </OwcEditableDropdown>

    {shouldShowAssistantSelect && (
      <div style={{ maxWidth: "520px", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
        <AssistantSelect
          width="100%"
          users={users}
          selectedAssistantId={draftAssistantId}
          onAssistantChange={onAssistantChange}
          required={true}
          disabled={saving}
        />
        <OwcInput
          type="number"
          label="Duration"
          value={draftAssistantDuration}
          disabled={saving}
          min="0.5"
          step="0.5"
          placeholder="Leave empty for auto"
          onInputChange={onAssistantDurationChange}
        >
          <div slot="suffix">h</div>
        </OwcInput>
      </div>
    )}

    {draftStatus === "REJECTED" && (
      <div style={{ width: "100%", maxWidth: "520px", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
        <label style={{ fontSize: "0.875rem", color: "#1F1F1F" }}>
          Rejection reason
        </label>
        <textarea
          value={draftRejectionReason}
          onChange={onRejectionReasonChange}
          disabled={saving}
          rows={3}
          style={{
            width: "100%",
            minHeight: "78px",
            resize: "vertical",
            padding: "8px 10px",
            border: "1px solid #B3B3B3",
            borderRadius: "4px",
            font: "inherit",
            boxSizing: "border-box",
          }}
        />
      </div>
    )}
  </div>
);

const ApprovalStatus = ({
  approvalStatus,
  allowEdit = false,
  onSaveStatus,
  onEditModeChange,
  users = [],
  assistantId = "",
  requiresAssistant = false,
}) => {
  const closeEditMode = () => {
    setIsEditMode(false);
    if (onEditModeChange) onEditModeChange(false);
  };

  const openEditMode = () => {
    setIsEditMode(true);
    if (onEditModeChange) onEditModeChange(true);
  };

  const [isEditMode, setIsEditMode] = useState(false);
  const [draftStatus, setDraftStatus] = useState(approvalStatus || "");
  const [draftAssistantId, setDraftAssistantId] = useState(assistantId || "");
  const [draftAssistantName, setDraftAssistantName] = useState("");
  const [draftAssistantDuration, setDraftAssistantDuration] = useState("");
  const [draftRejectionReason, setDraftRejectionReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraftStatus(approvalStatus || "");
  }, [approvalStatus]);

  useEffect(() => {
    setDraftAssistantId(assistantId || "");
  }, [assistantId]);

  useEffect(() => {
    if (!allowEdit && isEditMode) {
      setIsEditMode(false);
      if (onEditModeChange) onEditModeChange(false);
    }
  }, [allowEdit, isEditMode, onEditModeChange]);

  // AC-06: Reset assistant when status changes away from APPROVED
  // When user selects PENDING or REJECTED from APPROVED, automatically clear the assistant requirement
  useEffect(() => {
    if (isEditMode && approvalStatus === "APPROVED" && draftStatus !== "APPROVED" && draftAssistantId) {
      setDraftAssistantId("");
      setDraftAssistantName("");
    }
  }, [draftStatus, isEditMode, approvalStatus, draftAssistantId]);

  const hasChanges = draftStatus !== (approvalStatus || "");
  const shouldShowAssistantSelect = isAssistantSelectionRequired({
    draftStatus,
    approvalStatus,
    requiresAssistant,
    assistantId,
  });
  const isAssistantRequired = shouldShowAssistantSelect;
  const isRejectionReasonRequired = draftStatus === "REJECTED";
  const canSave = computeCanSave({
    hasChanges,
    isAssistantRequired,
    draftAssistantId,
    isRejectionReasonRequired,
    draftRejectionReason,
  });

  const handleSelectChange = (ev) => {
    if (ev?.detail === null || ev?.detail === undefined) return;
    const next = STATUS_OPTIONS[ev.detail];
    if (next) setDraftStatus(next);
  };

  const handleCancel = () => {
    setDraftStatus(approvalStatus || "");
    setDraftAssistantId(assistantId || "");
    setDraftAssistantName("");
    setDraftAssistantDuration("");
    setDraftRejectionReason("");
    closeEditMode();
  };

  const handleSave = async () => {
    if (!onSaveStatus) {
      closeEditMode();
      return;
    }
    setSaving(true);
    const success = await onSaveStatus(draftStatus, {
      assistantId: draftAssistantId || undefined,
      assistantName: draftAssistantName || undefined,
      assistantDuration: draftAssistantDuration,
      rejectionReason: draftRejectionReason.trim() || undefined,
    });
    setSaving(false);
    if (success) {
      closeEditMode();
    }
  };

  const handleStartEdit = (event) => {
    event.stopPropagation();
    openEditMode();
  };

  const handleCancelClick = (event) => {
    event.stopPropagation();
    handleCancel();
  };

  const handleSaveClick = (event) => {
    event.stopPropagation();
    handleSave();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
      <ApprovalStatusHeader
        allowEdit={allowEdit}
        isEditMode={isEditMode}
        saving={saving}
        hasChanges={hasChanges}
        canSave={canSave}
        onStartEdit={handleStartEdit}
        onCancel={handleCancelClick}
        onSave={handleSaveClick}
      />

      {!isEditMode && (
        <div>
          <ApprovedBadge status={approvalStatus} />
        </div>
      )}

      {isEditMode && (
        <ApprovalStatusEditPanel
          saving={saving}
          draftStatus={draftStatus}
          handleSelectChange={handleSelectChange}
          shouldShowAssistantSelect={shouldShowAssistantSelect}
          users={users}
          draftAssistantId={draftAssistantId}
          onAssistantChange={(userId, displayName) => {
            setDraftAssistantId(userId || "");
            setDraftAssistantName(displayName || "");
          }}
          draftAssistantDuration={draftAssistantDuration}
          onAssistantDurationChange={(event) => setDraftAssistantDuration(event.target?.value || "")}
          draftRejectionReason={draftRejectionReason}
          onRejectionReasonChange={(event) => setDraftRejectionReason(event.target.value)}
        />
      )}
    </div>
  );
};

ApprovalStatus.propTypes = {
  approvalStatus: PropTypes.string,
  allowEdit: PropTypes.bool,
  onSaveStatus: PropTypes.func,
  onEditModeChange: PropTypes.func,
  users: PropTypes.array,
  assistantId: PropTypes.string,
  requiresAssistant: PropTypes.bool,
};

export default ApprovalStatus;
